/**
 * Activity Log — the chambers audit trail.
 * Mounted at /admin/activity-log, /clerk/activity-log and /barrister/activity-log.
 *
 * Shows who did what, when, to which record, and — on expand — exactly what changed.
 *
 * WHAT THIS PAGE IS NOT: a chambers-wide feed. The server scopes every row to the caller
 * (audit-log.service.ts): a barrister sees their own actions plus actions on their matters, a
 * clerk sees theirs plus their assigned barristers', and only an admin sees auth events at all.
 * The empty state says "in your view" for that reason — a clerk seeing nothing has not found a
 * broken page, they have found the edge of their scope.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AUDIT_CATEGORIES,
  AUDIT_CATEGORY_LABEL,
  auditActionsInCategory,
  auditActionLabel,
  auditActionTone,
  type AuditCategory,
  type AuditActionValue,
  type AuditTone,
} from '@jurivoxa/shared';
import { useAuthStore } from '@/features/auth/store';
import { PageHeader } from '@/components/shared/PageHeader';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { TablePagination } from '@/components/shared/TablePagination';
import {
  fetchAuditLogs,
  fetchAuditLogSummary,
  downloadAuditLogCsv,
  type AuditLogEntry,
} from '@/lib/auditLogApi';
import { getApiErrorMessage } from '@/lib/api';
import {
  ClockCounterClockwise,
  CaretDown,
  CaretRight,
  FunnelSimple,
  CircleNotch,
  DownloadSimple,
  Tray,
} from '@phosphor-icons/react';

// ─── Presentation maps ────────────────────────────────────────────────────────

/**
 * Dot colour per tone. Semantic — money is not green-because-money, it is the colour we use
 * for a value actually moving, so a written-off note reads as `alert` and not as revenue.
 */
const TONE_DOT: Record<AuditTone, string> = {
  created: 'bg-emerald-500',
  changed: 'bg-sky-500',
  removed: 'bg-rose-500',
  viewed:  'bg-slate-400 dark:bg-slate-500',
  money:   'bg-violet-500',
  auth:    'bg-indigo-500',
  alert:   'bg-amber-500',
};

const ENTITY_LABEL: Record<string, string> = {
  Case: 'Matter', Matter: 'Matter', Hearing: 'Hearing', Contact: 'Contact',
  Document: 'Document', FeeRecord: 'Fee record', FeeNote: 'Fee note',
  CalendarEvent: 'Calendar', Task: 'Task', Message: 'Message',
  Allocation: 'Brief offer', User: 'User', Payment: 'Payment',
  CreditNote: 'Credit note', Barrister: 'Barrister', Judgment: 'Judgment',
};

// Internal columns that carry no meaning in a diff.
const SKIP_FIELDS = new Set(['updatedAt', 'createdAt', 'id', 'chambersId', 'deletedAt']);

const FIELD_LABEL: Record<string, string> = {
  caseName: 'Matter name', caseNumber: 'Matter number', status: 'Status',
  description: 'Description', feeType: 'Fee type', feeEstimate: 'Fee estimate',
  leadBarristerId: 'Lead barrister', outcome: 'Outcome', hearingDate: 'Hearing date',
  hearingType: 'Hearing type', firstName: 'First name', lastName: 'Last name',
  email: 'Email', memberType: 'Role', amount: 'Amount', totalAmount: 'Total',
  isBillable: 'Billable', title: 'Title', priority: 'Priority',
  dueDate: 'Due date', assignedToId: 'Assigned to', internalNotes: 'Internal notes',
  operation: 'Operation', reason: 'Reason',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtTime(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      return new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return v.replace(/_/g, ' ');
  }
  return JSON.stringify(v);
}

function getEntityRoute(entityType: string | null, entityId: string | null, basePath: string): string | null {
  if (!entityType || !entityId) return null;
  switch (entityType) {
    case 'Case': case 'Matter': return `${basePath}/matters/${entityId}`;
    case 'Hearing':   return `${basePath}/hearings/${entityId}`;
    case 'Contact':   return basePath === '/barrister' ? `${basePath}/clients/${entityId}` : `${basePath}/contacts/${entityId}`;
    case 'Barrister': return basePath === '/barrister' ? null : `${basePath}/barristers/${entityId}`;
    case 'Judgment':  return `${basePath}/judgments/${entityId}`;
  }
  return null;
}

function actorName(log: AuditLogEntry) {
  if (log.actor) return `${log.actor.firstName} ${log.actor.lastName}`;
  return log.actorEmail ?? 'System';
}

function actorInitials(log: AuditLogEntry) {
  if (log.actor) return `${log.actor.firstName[0]}${log.actor.lastName[0]}`;
  return 'SY';
}

/** The record this row is about, as a human would name it. */
function targetLabel(log: AuditLogEntry): string | null {
  const after = log.after as Record<string, unknown> | null;
  const before = log.before as Record<string, unknown> | null;
  const src = after ?? before;
  const name = (src?.caseNumber ?? src?.caseName ?? src?.title ?? src?.name ?? src?.subject) as string | undefined;
  if (name) return name;
  if (log.entityType) return ENTITY_LABEL[log.entityType] ?? log.entityType;
  return null;
}

/**
 * The one-line Details summary.
 *
 * Prefers metadata written for humans (`operation`, `reason`), then falls back to naming the
 * fields that changed — "Updated: Status, Fee estimate" tells a clerk more at a glance than a
 * raw JSON blob, and the full diff is one click away.
 */
function summariseLog(log: AuditLogEntry): string {
  const meta = log.metadata as Record<string, unknown> | null;
  if (meta) {
    if (typeof meta.operation === 'string') {
      return typeof meta.reason === 'string'
        ? `${meta.operation} — ${meta.reason}`
        : String(meta.operation);
    }
    if (typeof meta.reason === 'string') return String(meta.reason);
    if (typeof meta.event === 'string') return String(meta.event);
  }

  const before = log.before as Record<string, unknown> | null;
  const after = log.after as Record<string, unknown> | null;

  if (log.action === 'update' && before && after) {
    const changed = [...new Set([...Object.keys(before), ...Object.keys(after)])]
      .filter((k) => !SKIP_FIELDS.has(k) && JSON.stringify(before[k]) !== JSON.stringify(after[k]))
      .map((k) => FIELD_LABEL[k] ?? k.replace(/([A-Z])/g, ' $1').trim().toLowerCase());
    if (changed.length > 0) {
      const head = changed.slice(0, 3).join(', ');
      return changed.length > 3 ? `Updated: ${head} +${changed.length - 3} more` : `Updated: ${head}`;
    }
  }

  if (after?.status && log.action === 'create') return `Status: ${fmtVal(after.status)}`;
  return '';
}

/** Actions this member type can actually receive — the server filters non-admins to these. */
const OPERATIONAL_ACTIONS: AuditActionValue[] = ['create', 'update', 'delete', 'export', 'download', 'share'];

/**
 * Categories worth showing this user.
 *
 * A clerk picking "Sign-in & security" would get a guaranteed-empty table, because the server
 * intersects any category with what they may see. Hiding the empty categories is the honest
 * version of that rule rather than letting them discover it by finding nothing.
 */
function visibleCategories(isAdmin: boolean): AuditCategory[] {
  if (isAdmin) return [...AUDIT_CATEGORIES];
  return AUDIT_CATEGORIES.filter((c) =>
    auditActionsInCategory(c).some((a) => OPERATIONAL_ACTIONS.includes(a)),
  );
}

function visibleActions(category: AuditCategory, isAdmin: boolean): AuditActionValue[] {
  const all = auditActionsInCategory(category);
  return isAdmin ? all : all.filter((a) => OPERATIONAL_ACTIONS.includes(a));
}

// ─── Change diff (expanded row) ───────────────────────────────────────────────

function ChangeDiff({ log, isAdmin }: { log: AuditLogEntry; isAdmin: boolean }) {
  const before = log.before as Record<string, unknown> | null;
  const after  = log.after  as Record<string, unknown> | null;
  const meta   = log.metadata as Record<string, unknown> | null;

  const isUpdate = log.action === 'update';
  const isCreate = log.action === 'create';
  const isDelete = log.action === 'delete';

  const diffRows: { field: string; before: unknown; after: unknown }[] = [];

  if (isUpdate && before && after) {
    for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
      if (SKIP_FIELDS.has(key)) continue;
      if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
        diffRows.push({ field: key, before: before[key], after: after[key] });
      }
    }
  } else if (isCreate && after) {
    for (const [key, val] of Object.entries(after)) {
      if (SKIP_FIELDS.has(key) || val === null || val === undefined) continue;
      diffRows.push({ field: key, before: undefined, after: val });
    }
  } else if (isDelete && before) {
    for (const [key, val] of Object.entries(before)) {
      if (SKIP_FIELDS.has(key) || val === null || val === undefined) continue;
      diffRows.push({ field: key, before: val, after: undefined });
    }
  }

  const hasData = diffRows.length > 0 || (meta && Object.keys(meta).length > 0);
  if (!hasData) {
    return <p className="py-2 text-xs italic text-muted-foreground">No further detail recorded for this action.</p>;
  }

  return (
    <div className="space-y-4">
      {diffRows.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {isUpdate ? 'What changed' : isCreate ? 'What was created' : 'What was removed'}
          </p>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Field</th>
                  {isUpdate && <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Before</th>}
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">
                    {isUpdate ? 'After' : isCreate ? 'Value' : 'Had been'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {diffRows.map(({ field, before: bv, after: av }) => (
                  <tr key={field} className="bg-card">
                    <td className="px-3 py-2 font-medium text-foreground">
                      {FIELD_LABEL[field] ?? field.replace(/([A-Z])/g, ' $1').trim()}
                    </td>
                    {isUpdate && (
                      <td className="px-3 py-2 text-red-600 line-through decoration-red-300 dark:text-red-400">
                        {fmtVal(bv)}
                      </td>
                    )}
                    <td className={`px-3 py-2 ${isUpdate ? 'font-semibold text-emerald-700 dark:text-emerald-300' : 'text-foreground'}`}>
                      {fmtVal(isCreate ? av : isDelete ? bv : av)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {meta && Object.keys(meta).length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Additional context</p>
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <dl className="space-y-1">
              {Object.entries(meta).map(([k, v]) => (
                <div key={k} className="flex gap-3 text-xs">
                  <dt className="w-40 shrink-0 font-medium text-muted-foreground">
                    {FIELD_LABEL[k] ?? k.replace(/([A-Z])/g, ' $1').trim()}
                  </dt>
                  <dd className="break-words text-foreground">{fmtVal(v)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        {/* IP is private session data — admins only. */}
        {log.ip && isAdmin && <span>IP: {log.ip}</span>}
        {log.actor?.memberType && <span>Role: {log.actor.memberType.replace(/_/g, ' ')}</span>}
        {log.sequenceNumber && <span>Audit sequence #{log.sequenceNumber}</span>}
      </div>
    </div>
  );
}

// ─── Table row ────────────────────────────────────────────────────────────────

function LogRow({ log, basePath, isAdmin }: { log: AuditLogEntry; basePath: string; isAdmin: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const route = getEntityRoute(log.entityType, log.entityId, basePath);
  const target = targetLabel(log);
  const detail = summariseLog(log);

  return (
    <>
      <tr
        onClick={() => setExpanded((v) => !v)}
        className="cursor-pointer border-b border-border transition hover:bg-muted/30"
      >
        {/* Action */}
        <td className="px-2.5 py-2 align-top sm:px-4">
          <div className="flex items-start gap-2.5">
            <span className="mt-1 shrink-0 text-muted-foreground/40">
              {expanded ? <CaretDown className="h-3 w-3" /> : <CaretRight className="h-3 w-3" />}
            </span>
            <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${TONE_DOT[auditActionTone(log.action)]}`} />
            <span className="text-sm font-medium text-foreground">{auditActionLabel(log.action)}</span>
          </div>
        </td>

        {/* User */}
        <td className="px-2.5 py-2 align-top sm:px-4">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-700 dark:bg-brand-500/20 dark:text-brand-300">
              {actorInitials(log)}
            </span>
            <span className="text-sm text-foreground">{actorName(log)}</span>
          </div>
        </td>

        {/* Record */}
        <td className="px-2.5 py-2 align-top sm:px-4">
          {target ? (
            route ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate(route); }}
                className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
              >
                {target}
              </button>
            ) : (
              // No route means the record has no page, was deleted, or sits outside this
              // viewer's scope. Plain text beats a link that dead-ends in a 404.
              <span className="text-sm text-muted-foreground">{target}</span>
            )
          ) : (
            <span className="text-sm text-muted-foreground/50">—</span>
          )}
        </td>

        {/* Details */}
        <td className="px-2.5 py-2 align-top sm:px-4">
          <span className="line-clamp-2 text-xs text-muted-foreground">{detail || '—'}</span>
        </td>

        {/* Time */}
        <td className="whitespace-nowrap px-4 py-3 align-top text-xs tabular-nums text-muted-foreground">
          {fmtTime(log.createdAt)}
        </td>
      </tr>

      {expanded && (
        <tr className="border-b border-border bg-muted/20">
          <td colSpan={5} className="px-4 py-4 sm:px-12">
            <ChangeDiff log={log} isAdmin={isAdmin} />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Years to offer. The audit ledger cannot be pruned, so this only ever grows forward. */
function yearOptions(): number[] {
  const now = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => now - i);
}

/** year + month → the ISO range the API expects. Month is 1-based; 0 means "whole year". */
function toRange(year: string, month: string): { from?: string; to?: string } {
  if (!year) return {};
  const y = Number(year);
  if (!month) {
    return {
      from: new Date(Date.UTC(y, 0, 1)).toISOString(),
      to: new Date(Date.UTC(y, 11, 31, 23, 59, 59)).toISOString(),
    };
  }
  const m = Number(month) - 1;
  return {
    from: new Date(Date.UTC(y, m, 1)).toISOString(),
    // Day 0 of the NEXT month is the last day of this one — correct for every month length
    // and for February in a leap year, which a hardcoded 28/30/31 table is not.
    to: new Date(Date.UTC(y, m + 1, 0, 23, 59, 59)).toISOString(),
  };
}

export default function ActivityLogPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.memberType === 'admin';
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const isBarrister = ['silk', 'junior', 'pupil', 'door_tenant'].includes(user?.memberType ?? '');
  const basePath = isBarrister ? '/barrister' : isClerk ? '/clerk' : '/admin';

  const [page, setPage]         = useState(1);
  const [category, setCategory] = useState<AuditCategory | ''>('');
  const [action, setAction]     = useState<AuditActionValue | ''>('');
  const [year, setYear]         = useState('');
  const [month, setMonth]       = useState('');
  const [mine, setMine]         = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Rows per page is the reader's choice here as in every other list. 20 is the nearest of
  // the shared PAGE_SIZE_OPTIONS to the 25 this page used to hard-code.
  const [limit, setLimit] = useState(20);
  const categories = visibleCategories(isAdmin);
  const range = toRange(year, month);

  const filters = {
    ...(category && { category }),
    ...(action && { action }),
    ...range,
    ...(mine && user?.id && { actorId: user.id }),
  };

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['activity-log-full', page, limit, category, action, year, month, mine],
    queryFn: () => fetchAuditLogs({ page, limit, ...filters }),
    staleTime: 30_000,
    // Keep the previous page rendered while the next one loads. Blanking the table on every
    // filter change made the toolbar feel broken; dimming it reads as "working".
    placeholderData: keepPreviousData,
  });

  const { data: summary } = useQuery({
    queryKey: ['activity-log-summary', mine],
    queryFn: () => fetchAuditLogSummary(mine),
    staleTime: 60_000,
  });

  const logs = data?.logs ?? [];
  const pagination = data?.pagination;
  const hasFilters = !!(category || action || year || month || mine);

  function resetFilters() {
    setPage(1); setCategory(''); setAction(''); setYear(''); setMonth(''); setMine(false);
  }

  async function handleDownload() {
    setDownloading(true);
    try {
      const { truncated, rowCount } = await downloadAuditLogCsv(filters);
      toast.success(
        truncated
          ? `Downloaded the first ${rowCount.toLocaleString()} rows — narrow the filter to export the rest.`
          : `Downloaded ${rowCount.toLocaleString()} rows.`,
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not export the activity log'));
    } finally {
      setDownloading(false);
    }
  }

  // md:flex-1 is what keeps Download CSV on the first row at iPad width. Sized to their
  // content the four selects measure ~380px, which with the funnel, the checkbox and the gaps
  // fills the line and pushes the button onto one of its own. `flex-1` gives them a basis of 0,
  // so they never force a wrap — they simply share whatever is left after the fixed items and
  // the button. Content width returns at lg, where the row is wide enough for everything.
  const SEL = 'h-8 w-full min-w-0 appearance-none rounded-lg border border-border bg-card px-2.5 text-xs text-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-200 sm:w-auto md:min-w-0 md:flex-1 lg:flex-none';

  const subtitleParts: string[] = [];
  if (summary?.today) subtitleParts.push(`${summary.today} today`);
  if (summary?.thisWeek) subtitleParts.push(`${summary.thisWeek} this week`);

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Activity Log"
        subtitle={
          [
            isAdmin
              ? 'Every action recorded — who, what, and when'
              : 'Actions across your matters, hearings and fees',
            ...subtitleParts,
          ].join(' · ')
        }
        className="mb-4 sm:mb-2 lg:mb-4"
      />

      {/* Toolbar */}
      <div className="mb-4 rounded-lg bg-muted/40 p-2.5 sm:mb-2 sm:flex sm:flex-wrap sm:items-center sm:gap-2 lg:mb-4">
        <FunnelSimple className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />

        {/* The four selects: a 2x2 grid of equal widths on a phone, a flowing row from sm up. */}
        <div className="grid grid-cols-2 gap-2 sm:contents">

        {/* Category → action. Two selects rather than one list of 67. */}
        <select
          value={category}
          onChange={(e) => { setCategory(e.target.value as AuditCategory | ''); setAction(''); setPage(1); }}
          className={SEL}
          aria-label="Activity category"
        >
          <option value="">All activity</option>
          {categories.map((c) => (
            <option key={c} value={c}>{AUDIT_CATEGORY_LABEL[c]}</option>
          ))}
        </select>

        <select
          value={action}
          onChange={(e) => { setAction(e.target.value as AuditActionValue | ''); setPage(1); }}
          className={SEL}
          disabled={!category}
          aria-label="Action"
          title={category ? undefined : 'Choose a category first'}
        >
          <option value="">{category ? 'All actions in category' : 'All actions'}</option>
          {category && visibleActions(category, isAdmin).map((a) => (
            <option key={a} value={a}>{auditActionLabel(a)}</option>
          ))}
        </select>

        <select
          value={year}
          onChange={(e) => { setYear(e.target.value); if (!e.target.value) setMonth(''); setPage(1); }}
          className={SEL}
          aria-label="Year"
        >
          <option value="">All years</option>
          {yearOptions().map((y) => <option key={y} value={y}>{y}</option>)}
        </select>

        <select
          value={month}
          onChange={(e) => { setMonth(e.target.value); setPage(1); }}
          className={SEL}
          disabled={!year}
          aria-label="Month"
          title={year ? undefined : 'Choose a year first'}
        >
          <option value="">All months</option>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>

        </div>

        <label className="mt-2 flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground sm:mt-0">
          <input
            type="checkbox"
            checked={mine}
            onChange={(e) => { setMine(e.target.checked); setPage(1); }}
            className="rounded"
          />
          My actions only
        </label>

        <div className="mt-2 flex items-center justify-between gap-2 sm:mt-0 sm:contents">
          {hasFilters ? (
            <button onClick={resetFilters} className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">
              Clear filters
            </button>
          ) : <span className="sm:hidden" />}

        <button
          onClick={handleDownload}
          disabled={downloading || logs.length === 0}
          className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
        >
          {downloading
            ? <CircleNotch className="h-3.5 w-3.5 animate-spin" />
            : <DownloadSimple className="h-3.5 w-3.5" />}
          Download CSV
        </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <ClockCounterClockwise className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">
            {hasFilters ? 'Filtered activity' : 'All activity'}
            {pagination && (
              <span className="ml-1.5 font-normal text-muted-foreground tabular-nums">
                ({pagination.total.toLocaleString()})
              </span>
            )}
          </h2>
          {isFetching && !isLoading && <CircleNotch className="h-3.5 w-3.5 animate-spin text-muted-foreground/50" />}
        </div>

        {isError ? (
          <QueryErrorState what="activity" onRetry={() => void refetch()} />
        ) : isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <CircleNotch className="h-5 w-5 animate-spin" />
            Loading activity…
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <Tray className="mb-2 h-8 w-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">
              {hasFilters ? 'No activity matches these filters' : 'No activity in your view yet'}
            </p>
            {!hasFilters && !isAdmin && (
              <p className="mt-1 max-w-sm text-xs text-muted-foreground/70">
                You see your own actions and actions on the matters you work on.
              </p>
            )}
            {hasFilters && (
              <button onClick={resetFilters} className="mt-2 text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className={`overflow-x-auto transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left">
                  <th className="px-2.5 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:px-4">Action</th>
                  <th className="px-2.5 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:px-4">User</th>
                  <th className="px-2.5 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:px-4">Record</th>
                  <th className="px-2.5 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:px-4">Details</th>
                  <th className="px-2.5 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:px-4">Time</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <LogRow key={log.id} log={log} basePath={basePath} isAdmin={isAdmin} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination — the shared bar, so this list offers the same rows-per-page choice and
          the same wording as every other list. It replaced a hand-rolled pager that showed five
          numbered buttons and no way to change the page size. */}
      {(pagination?.total ?? 0) > 0 && (
        <TablePagination
          className="mt-4 rounded-xl border border-border bg-card"
          page={page}
          pageSize={limit}
          total={pagination?.total ?? 0}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setLimit(size); setPage(1); }}
        />
      )}
    </div>
  );
}
